import FilePreviewDialog from "@/components/filePreviewDialog/filePreviewDialog";
// eslint-disable-next-line max-len
import { FullScreenPleaseSelectMandateNotice } from "@/components/fullScreenPleaseSelectMandateNotice/FullScreenPleaseSelectMandateNotice";
import Header from "@/components/header/header";
import PageSwitcher from "@/components/pageSwitcher/PageSwitcher";
import TextButton from "@/components/textButton/TextButton";
import { TopBarMobile } from "@/components/topBarMobile/topBarMobile";
import { translate, TranslatorKeys } from "@/config/translator";
import { formatDateFromLang, getFileFAIcon } from "@/config/utils";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Text,
} from "@fluentui/react-components";
import { faSearch } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { filesize } from "filesize";
import { usePspDocuments } from "./pspDocuments/usePspDocuments";
import { PspDocument } from "@/api/openapi";
import Skeleton from "react-loading-skeleton";
import { NO_VALUE } from "@/config/constants";

export default function PspDocuments() {
  const {
    columns,
    filterOptionsGroups,
    isTablet,
    onDownload,
    onOpenPreview,
    selectedMandateId,
    startSearchingMobile,
    filterValues,
    documents,
    searchQuery,
    setFilterValues,
    setSearchQuery,
    lang,
    currentFilePreview,
    isSearchingMobile,
    onClosePreview,
    page,
    searchBoxMobileRef,
    setPage,
    totalPages,
    setIsSearchingMobile,
  } = usePspDocuments();

  if (!selectedMandateId) {
    return <FullScreenPleaseSelectMandateNotice />;
  }

  if (isTablet)
    return (
      <>
        <div className="p-10 flex flex-col">
          {/* HEADER */}
          <Header
            text="pspDocuments"
            onChangeSearch={setSearchQuery}
            searchValue={searchQuery}
            setFilters={setFilterValues}
            filterValues={filterValues}
            filterOptionsGroups={filterOptionsGroups}
          />
          {documents && documents.length === 0 ? (
            <p className="text-font-secondary">
              {translate("noPspDocuments", lang)}
            </p>
          ) : (
            <div className="overflow-x-scroll w-[calc(100vw-21rem)] desktop:w-full">
              <Table className="min-w-3xl table-fixed">
                <colgroup>
                  {columns.map(({ className }) => (
                    <col className={className} />
                  ))}
                </colgroup>
                <TableHeader>
                  <TableRow>
                    {columns.map(({ key, label }) => (
                      <TableHeaderCell key={`users-table-header-${key}`}>
                        {label}
                      </TableHeaderCell>
                    ))}
                  </TableRow>
                </TableHeader>

                {/* TABLE BODY */}
                <TableBody>
                  {(documents ?? Array.from({ length: 20 })).map(
                    (document: PspDocument | undefined, index) => (
                      <TableRow
                        className={
                          index % 2 === 0 ? "bg-white" : "bg-canvas-secondary"
                        }
                        key={`uploads-webapp-table-row-${document?.id ?? index}`}
                      >
                        {/* ICON */}
                        <TableCell>
                          {document ? (
                            <FontAwesomeIcon
                              size="xl"
                              className="text-font-secondary"
                              icon={getFileFAIcon(document.mime ?? "")}
                            />
                          ) : (
                            <Skeleton
                              circle
                              width={30}
                              className="aspect-square"
                            />
                          )}
                        </TableCell>

                        {/* DOCUMENT TYPE */}
                        <TableCell>
                          <Text>
                            {document ? (
                              [
                                "ACCOUNTING_SONSTIGE",
                                "PAYROLL_SONSTIGE",
                              ].includes(document.docType) ? (
                                document.name
                              ) : (
                                translate(document.docType, lang)
                              )
                            ) : (
                              <Skeleton />
                            )}
                          </Text>
                        </TableCell>

                        {/* MONTH */}
                        <TableCell>
                          <Text>
                            {document ? (
                              document.month ? (
                                translate(
                                  `month${document.month}` as TranslatorKeys,
                                  lang,
                                )
                              ) : (
                                NO_VALUE
                              )
                            ) : (
                              <Skeleton />
                            )}
                          </Text>
                        </TableCell>

                        {/* YEAR */}
                        <TableCell>
                          <Text>{document?.year ?? <Skeleton />}</Text>
                        </TableCell>

                        {/* CREATED AT */}
                        <TableCell>
                          <Text>
                            {document ? (
                              formatDateFromLang(
                                new Date(document.createdAt),
                                lang,
                              )
                            ) : (
                              <Skeleton />
                            )}
                          </Text>
                        </TableCell>

                        {/* SIZE */}
                        <TableCell>
                          <Text>
                            {document ? filesize(document.size) : <Skeleton />}
                          </Text>
                        </TableCell>

                        {/* ACTION */}
                        <TableCell>
                          <TextButton
                            disabled={!document}
                            onClick={
                              document
                                ? () => onOpenPreview(document.id)
                                : undefined
                            }
                          >
                            {translate("details", lang)}
                          </TextButton>
                        </TableCell>
                      </TableRow>
                    ),
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
        <div className="w-full flex-row flex justify-end pr-10 pb-10">
          <PageSwitcher
            currentPage={page ?? 0}
            totalPages={totalPages}
            setCurrentPage={setPage}
          />
        </div>
        {currentFilePreview && (
          <FilePreviewDialog
            onClose={onClosePreview}
            file={currentFilePreview}
            isOpen={!!currentFilePreview}
            onDownload={onDownload}
          />
        )}
      </>
    );

  return (
    <>
      <TopBarMobile
        header="pspDocuments"
        onChangeSearch={setSearchQuery}
        searchValue={searchQuery}
        isSearching={isSearchingMobile}
        searchBoxRef={searchBoxMobileRef}
        stopSearching={() => setIsSearchingMobile(false)}
        rightSide={
          <div className="flex flex-row gap-12">
            <FontAwesomeIcon
              icon={faSearch}
              onClick={startSearchingMobile}
              className="text-font-white text-2xl"
            />
          </div>
        }
      />
      <div className="p-6"></div>
    </>
  );
}
